import React, { useState, useCallback } from 'react';
import {
  Box, Tab, Tabs, CircularProgress, Alert, Button,
  TextField, MenuItem, Grid, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip
} from '@mui/material';

import employeePunchesAPI from '../../api/employeePunchesAPI';
import { FilterValues } from '../../types/dynamicTypes';

interface PunchFilters {
  organizationId?: string;
  employeeId?: string;
  startDate?: Date | null;
  endDate?: Date | null;
  modeOfEntry?: string;
  punchType?: string;
  viewType?: string;
}

const EmployeePunchesModule: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [filters, setFilters] = useState<PunchFilters>({ startDate: null, endDate: null });
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setData([]);
    setError(null);
  };

  const handleFilterChange = (key: keyof PunchFilters) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilters(prev => ({ ...prev, [key]: event.target.value }));
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params: FilterValues = {
        organizationId: filters.organizationId,
        employeeId: filters.employeeId,
        startDate: filters.startDate ? new Date(filters.startDate).toISOString() : null,
        endDate: filters.endDate ? new Date(filters.endDate).toISOString() : null,
        modeOfEntry: filters.modeOfEntry,
        punchType: filters.punchType,
        viewType: filters.viewType
      };

      let response: any[] = [];
      switch (activeTab) {
        case 0:
          response = await employeePunchesAPI.getEmployeePunches(params);
          break;
        case 1:
          response = await employeePunchesAPI.getWhosInEmployee(params);
          break;
        case 2:
          response = await employeePunchesAPI.getWhosInGroup(params);
          break;
        case 3:
          response = await employeePunchesAPI.getMobileCheckinReport(params);
          break;
      }

      setData(response);
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [activeTab, filters]);

  // === Table renderers for each tab ===

  const renderTotalPunchTable = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Organization</TableCell>
            <TableCell>Employee Code</TableCell>
            <TableCell>Employee Name</TableCell>
            <TableCell>Date</TableCell>
            <TableCell>Time</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Mode of Entry</TableCell>
            <TableCell>Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, idx) => {
            // You may choose CheckInTime or CheckOutTime for "Time" column; here showing CheckInTime as example
            const time = row.checkInTime || row.checkOutTime;
            return (
              <TableRow key={idx}>
                <TableCell>{row.organizationId || '-'}</TableCell>
                <TableCell>{row.employeeCode || '-'}</TableCell>
                <TableCell>{row.employeeName || '-'}</TableCell>
                <TableCell>{row.date ? new Date(row.date).toLocaleDateString() : '-'}</TableCell>
                <TableCell>{time ? new Date(time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}</TableCell>
                <TableCell>{row.punchType || (row.checkOutTime ? 'CHECK OUT' : 'CHECK IN') || '-'}</TableCell>
                <TableCell>{row.modeOfEntry || '-'}</TableCell>
                <TableCell>
                  <Button size="small" variant="outlined">Action</Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderWhosInEmployeeTable = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Emp Code</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Branch</TableCell>
            <TableCell>Date</TableCell>
            <TableCell>First Punch</TableCell>
            <TableCell>Recent Punch</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, idx) => {
            const status = row.status || (row.firstPunch ? 'IN' : 'NO PUNCH');
            return (
              <TableRow key={idx}>
                <TableCell>{row.employeeCode || '-'}</TableCell>
                <TableCell>{row.employeeName || '-'}</TableCell>
                <TableCell>{row.department || '-'}</TableCell>
                <TableCell>{row.date ? new Date(row.date).toLocaleDateString() : '-'}</TableCell>
                <TableCell>{row.checkInTime ? new Date(row.checkInTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}</TableCell>
                <TableCell>{row.checkOutTime ? new Date(row.checkOutTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}</TableCell>
                <TableCell>
                  <Chip
                    label={row.checkOutTime ? 'OUT' : status}
                    size="small"
                    color={(row.checkOutTime ? 'OUT' : status).toUpperCase() === 'IN' ? 'success' : 'error'}
                    sx={{ fontWeight: 'bold' }}
                  />
                </TableCell>
                <TableCell>
                  <Button size="small" variant="text" sx={{ textTransform: 'none' }}>View Punches</Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderWhosInGroupTable = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>All Name</TableCell>
            <TableCell>Date</TableCell>
            <TableCell>In</TableCell>
            <TableCell>Out</TableCell>
            <TableCell>No Punch</TableCell>
            <TableCell>On Leave</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, idx) => (
            <TableRow key={idx}>
              <TableCell>{row.allName || '-'}</TableCell>
              <TableCell>{row.date ? new Date(row.date).toLocaleDateString() : '-'}</TableCell>
              <TableCell>{row.in || '-'}</TableCell>
              <TableCell>{row.out || '-'}</TableCell>
              <TableCell>{row.noPunch || '-'}</TableCell>
              <TableCell>{row.onLeave || '-'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderMobileCheckinTable = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Date</TableCell>
            <TableCell>Employee Code</TableCell>
            <TableCell>Employee Name</TableCell>
            <TableCell>Location Type</TableCell>
            <TableCell>Entity Name</TableCell>
            <TableCell>Location Name</TableCell>
            <TableCell>Check In Time</TableCell>
            <TableCell>Check Out Time</TableCell>
            <TableCell>Duration (in Mins)</TableCell>
            <TableCell>Duration (in HH:MM)</TableCell>
            <TableCell>Checkout Location Name</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, idx) => (
            <TableRow key={idx}>
              <TableCell>{row.checkinDate ? new Date(row.checkinDate).toLocaleDateString() : '-'}</TableCell>
              <TableCell>{row.employeeCode || '-'}</TableCell>
              <TableCell>{row.employeeName || '-'}</TableCell>
              <TableCell>{row.entityType || '-'}</TableCell>
              <TableCell>{row.entityName || '-'}</TableCell>
              <TableCell>{row.location || '-'}</TableCell>
              <TableCell>{row.punchTime ? new Date(row.punchTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}</TableCell>
              <TableCell>-</TableCell>
              <TableCell>-</TableCell>
              <TableCell>-</TableCell>
              <TableCell>{row.remarks || '-'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // Main table renderer by active tab
  const renderTable = () => {
    switch (activeTab) {
      case 0:
        return renderTotalPunchTable();
      case 1:
        return renderWhosInEmployeeTable();
      case 2:
        return renderWhosInGroupTable();
      case 3:
        return renderMobileCheckinTable();
      default:
        return null;
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Tabs value={activeTab} onChange={handleTabChange}>
        <Tab label="View Total Punch" />
        <Tab label="Who's In (Employee)" />
        <Tab label="Who's In (Group)" />
        <Tab label="Mobile Checkin" />
      </Tabs>

      {/* Filters */}
      <Box sx={{ m: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label="Organization"
              value={filters.organizationId || ''}
              onChange={handleFilterChange('organizationId')}
              size="small"
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label="Employee"
              value={filters.employeeId || ''}
              onChange={handleFilterChange('employeeId')}
              size="small"
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={filters.startDate ? filters.startDate.toISOString().substring(0, 10) : ''}
              onChange={e => setFilters(prev => ({ ...prev, startDate: e.target.value ? new Date(e.target.value) : null }))}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={filters.endDate ? filters.endDate.toISOString().substring(0, 10) : ''}
              onChange={e => setFilters(prev => ({ ...prev, endDate: e.target.value ? new Date(e.target.value) : null }))}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label="Mode of Entry"
              select
              value={filters.modeOfEntry || ''}
              onChange={e => setFilters(prev => ({ ...prev, modeOfEntry: e.target.value }))}
              size="small"
            >
              <MenuItem value="">Any</MenuItem>
              <MenuItem value="Device">Device</MenuItem>
              <MenuItem value="Mobile">Mobile</MenuItem>
              <MenuItem value="Manual">Manual</MenuItem>
            </TextField>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label="Punch Type"
              select
              value={filters.punchType || ''}
              onChange={e => setFilters(prev => ({ ...prev, punchType: e.target.value }))}
              size="small"
            >
              <MenuItem value="">Any</MenuItem>
              <MenuItem value="In">In</MenuItem>
              <MenuItem value="Out">Out</MenuItem>
            </TextField>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label="View Type"
              select
              value={filters.viewType || ''}
              onChange={e => setFilters(prev => ({ ...prev, viewType: e.target.value }))}
              size="small"
            >
              <MenuItem value="">Any</MenuItem>
              <MenuItem value="Compact">Compact</MenuItem>
              <MenuItem value="Detailed">Detailed</MenuItem>
            </TextField>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Button fullWidth variant="contained" onClick={fetchData} disabled={loading}>
              View
            </Button>
          </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
            <Button fullWidth variant="outlined" onClick={() => setFilters({ startDate: null, endDate: null })} disabled={loading}>
              Clear
            </Button>
          </Grid>
        </Grid>
      </Box>

      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      )}

      {error && <Alert severity="error">{error}</Alert>}

      <Box sx={{ p: 2 }}>
        {data.length === 0 && !loading ? <Alert severity="info">No data available</Alert> : renderTable()}
      </Box>
    </Box>
  );
};

export default EmployeePunchesModule;
